import { useLocalStorageWithSubscription } from "@sundaeswap/react-hooks";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import { useCallback, useMemo } from "react";

export type TDepositHistoryStatus =
  | "new"        // Request sent to hydrozoa, awaiting registration
  | "pending"    // Registered in L2 ledger, awaiting block confirmation
  | "confirmed"  // L2 block confirmed, refund tx available, submitting L1 tx
  | "settling"   // L1 tx submitted to Cardano, awaiting on-chain confirmation
  | "on-chain"   // L1 tx confirmed on Cardano, awaiting L2 absorption
  | "absorbed"   // Deposit absorbed into L2, funds available
  | "rejected"   // Deposit rejected by the network
  | "refunding"  // Refund tx submitted, awaiting on-chain confirmation
  | "refunded"   // Refund confirmed on-chain, funds returned to wallet
  | "failed";    // Processing error

export interface IDepositHistoryTx {
  /** Signed L1 deposit tx, empty until the wallet signature is collected */
  cbor: string;
  /** Unsigned L1 deposit tx, kept so signing can be deferred (and recovered
   *  after a reload) until the refund tx has been provisioned */
  unsignedCbor?: string;
  refundCbor: string;
  /** L1 tx id of the multisigned refund tx, for display/download in the UI */
  refundTxHash?: string;
  /** True once the deposit's L2 block is hard confirmed (multisigned by the
   *  head peers, i.e. the BlockConfirmed event reached us over the websocket) */
  hardConfirmed?: boolean;
  hash: string;
  /** How many times we have handed this tx to the wallet for submission.
   *  Diagnostic only -- what stops the retries is the tx's own TTL, not a
   *  count. Recorded because "how many attempts did it take" is the first
   *  question worth asking about a deposit that took a long time to settle. */
  submitAttempts?: number;
  status: TDepositHistoryStatus;
  errorMessage?: string;
  /** Unix timestamp (seconds) when the deposit is estimated to be absorbed */
  estimatedAbsorptionTime?: number;
}

export type TRequestId = string;

export type TPendingDepositMap = Map<TRequestId, IDepositHistoryTx>;

const EMPTY_MAP: TPendingDepositMap = new Map();

/**
 * Read the current deposit map directly from localStorage.
 * This bypasses React state closures which go stale inside
 * async operations (mutations, event handlers, etc.).
 */
function readFromStorage(key: string): TPendingDepositMap {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Map();
    return new Map(JSON.parse(raw));
  } catch {
    return new Map();
  }
}

function writeToStorage(key: string, map: TPendingDepositMap) {
  localStorage.setItem(key, JSON.stringify([...map.entries()]));
}

export const useDepositTxs = () => {
  const { mainAddress } = useWalletObserver();

  const storageKey = useMemo(() => {
    if (!mainAddress) return null;
    return `depositTxs_${mainAddress.slice(-12)}`;
  }, [mainAddress]);

  const [stored, setStored, clearStored] =
    useLocalStorageWithSubscription<TPendingDepositMap>(
      storageKey ?? "depositTxs_unused",
      new Map(),
      {
        serializer(value) {
          return JSON.stringify([...value.entries()]);
        },
        deserializer(value) {
          return new Map(JSON.parse(value));
        },
      },
    );

  const active = storageKey !== null;
  const depositTxs = active ? stored : EMPTY_MAP;

  const addDepositTx = useCallback(
    (id: bigint, data: IDepositHistoryTx) => {
      if (!storageKey) {
        throw new Error("addDepositTx called without a connected wallet");
      }
      // Read-modify-write directly from localStorage to avoid stale closures
      const current = readFromStorage(storageKey);
      current.set(id.toString(), data);
      writeToStorage(storageKey, current);
      setStored(current);
    },
    [storageKey, setStored],
  );

  const updateDepositTx = useCallback(
    (id: bigint, data: Partial<IDepositHistoryTx>): IDepositHistoryTx => {
      if (!storageKey) {
        throw new Error("updateDepositTx called without a connected wallet");
      }
      // Read-modify-write directly from localStorage to avoid stale closures
      const current = readFromStorage(storageKey);
      const existing = current.get(id.toString());

      const merged: IDepositHistoryTx = existing
        ? { ...existing, ...data }
        : { cbor: "", hash: "", refundCbor: "", status: "new", ...data };

      current.set(id.toString(), merged);
      writeToStorage(storageKey, current);
      setStored(current);
      return merged;
    },
    [storageKey, setStored],
  );

  return {
    depositTxs,
    setDepositTxs: setStored,
    clearDepositTxs: clearStored,
    addDepositTx,
    updateDepositTx,
  };
};
